import React from "react";
import { createPortal } from "react-dom";

type DensityKey = "balanced" | "md" | "lg";

// Density presets
const densityPresets: Record<DensityKey, { count: number; label: string }> = {
  balanced: { count: 250, label: "Balanced" },
  md: { count: 50, label: "Medium" },
  lg: { count: 50, label: "Large" }
};

export default function SnowfallAnimation({ 
  density = "balanced",
  animationSpeed = 1,
  asOverlay = false 
}: {
  density?: DensityKey;
  animationSpeed?: number;
  asOverlay?: boolean;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [animationKey, setAnimationKey] = React.useState(0);

  // Force re-render when props change
  React.useEffect(() => {
    setAnimationKey(prev => prev + 1);
  }, [density, animationSpeed]);

  // Generate snowflake data
  const generateSnowflakes = (count: number) => {
    return Array.from({ length: count }, (_, index) => ({
      id: `snowflake-${index}-${animationKey}`,
      size: 0.4 + Math.random() * 1.4,
      left: -20 + Math.random() * 120,
      delay: -Math.random() * 10,
      duration: (10 + Math.random() * 5) / animationSpeed,
      blur: Math.random() * 1.5,
      drift: Math.random() * 60 - 30
    }));
  };

  const snowflakes = generateSnowflakes(densityPresets[density].count);

  // Manual CSS injection for fallback
  const injectKeyframes = (keyframes: string, keyframeId: string) => {
    const existingStyle = document.getElementById(keyframeId);
    if (existingStyle) existingStyle.remove();
    
    const style = document.createElement('style');
    style.id = keyframeId;
    style.textContent = keyframes;
    document.head.appendChild(style);
    
    void document.body.offsetWidth; // Force reflow
  };

  // Render with Styletron or manual CSS
  const renderSnowflakes = () => {
    // Manual CSS injection and rendering
    snowflakes.forEach((flake, index) => {
      const animationName = `snowfall-${index}-${animationKey}`;
      const keyframes = `
        @keyframes ${animationName} {
          0% { 
            transform: translateY(-10vh) translateX(0) rotate(0deg); 
            opacity: 0;
          }
          10% { opacity: 0.8; }
          90% { opacity: 0.8; }
          100% { 
            transform: translateY(110vh) translateX(${flake.drift}vw) rotate(360deg); 
            opacity: 0;
          }
        }
      `;
      injectKeyframes(keyframes, `keyframe-${index}-${animationKey}`);
    });

    return snowflakes.map((flake, index) => (
      <span
        key={flake.id}
        style={{
          position: "absolute",
          left: `${flake.left}vw`,
          top: "0vh",
          fontSize: `${flake.size}rem`,
          filter: `blur(${flake.blur}px)`,
          animation: `${flake.duration}s snowfall-${index}-${animationKey} linear infinite`,
          animationDelay: `${flake.delay}s`,
          opacity: 0.8,
          userSelect: "none",
          pointerEvents: "none"
        }}
      >
        ❄️
      </span>
    ));
  };

  const content = (
    <div 
      ref={containerRef}
      className={"snowflake-area"}
      style={{ 
        position: "absolute", 
        top: 0, 
        left: 0, 
        width: "100%", 
        height: "100%", 
        pointerEvents: "none",
        zIndex: 9999,
        overflow: "visible"
      }}
    >
      {renderSnowflakes()}
    </div>
  );

  // Render as portal overlay or inline
  if (asOverlay && typeof window !== 'undefined') {
    let portalRoot = document.getElementById('snowfall-overlay-root');
    if (!portalRoot) {
      portalRoot = document.createElement('div');
      portalRoot.id = 'snowfall-overlay-root';
      portalRoot.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 9999;
      `;
      document.body.appendChild(portalRoot);
    }
    return createPortal(content, portalRoot);
  }

  return content;
}
